import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import OperatorClaims from './OperatorClaims';
import OperatorCarrierAccounts from './OperatorCarrierAccounts';
import OperatorReceiving from './OperatorReceiving';
import OperatorPutaway from './OperatorPutaway';
import OperatorLocations from './OperatorLocations';
import WarehouseScanInput from '@/components/operator/WarehouseScanInput';

// STEP 2-P7-O · Canonical variant helper — mirrors backend
// `inventory._variant_key`. `seller_orders.items[]` NEVER stores a
// `variant_key` field, only an `option` dict. Reading `it.variant_key`
// therefore always fell through to "default", breaking scan-match
// key equality against backend responses (which return canonical
// values like "색상=아이보리"). Local definition avoids cross-file
// coupling; identical rule (alpha-sorted keys, drop null/""/undef,
// join with "|", empty → "default").
const canonicalVk = (option) => {
  if (!option || typeof option !== 'object') return 'default';
  const keys = Object.keys(option).sort();
  const parts = [];
  for (const k of keys) {
    const v = option[k];
    if (v === null || v === undefined || v === '') continue;
    parts.push(`${k}=${v}`);
  }
  return parts.length ? parts.join('|') : 'default';
};

/**
 * STEP 2-P7-C · Operator (3PL) Dashboard Shell.
 *
 * Pure UI aggregation over existing backend endpoints:
 *  - GET  /api/operator/me                        (role + scope)
 *  - GET  /api/operator/dashboard/summary         (queue counts)
 *  - GET  /api/operator/orders                    (queues)
 *  - GET  /api/operator/claims                    (returns / exchange)
 *  - Reuses <OperatorClaims/> and <OperatorCarrierAccounts/> pages.
 *  - No new backend engine. RBAC enforced server-side.
 */

const ROLE_MENU = {
  '3PL_ADMIN':          ['home','orders','pick','inspect','pack','ship','combined-requests','receiving','putaway','locations','claims','carriers','warehouses','staff'],
  'WAREHOUSE_MANAGER':  ['home','orders','pick','inspect','pack','ship','combined-requests','receiving','putaway','locations','claims','carriers','warehouses'],
  'PICKER':             ['home','pick','exchange-pick','putaway'],
  'INSPECTOR':          ['home','inspect','claims'],
  'PACKER':             ['home','pack','putaway'],
};

const MENU_LABELS = {
  'home': '홈',
  'orders': '주문 작업',
  'pick': '피킹',
  'inspect': '검수',
  'pack': '포장',
  'ship': '포장 / 출고',
  'combined-requests': '판매자 합포 요청',
  'claims': '반품 / 교환',
  'exchange-pick': '교환 피킹',
  'carriers': '택배 계약 관리',
  'warehouses': '창고 관리',
  'staff': '작업자 관리',
  'receiving': '입고',
  'putaway': '적치',
  'locations': '로케이션',
};

const ROLE_LABEL = {
  '3PL_ADMIN': '3PL 관리자', 'WAREHOUSE_MANAGER': '창고 매니저',
  'PICKER': '피킹', 'INSPECTOR': '검수', 'PACKER': '포장',
};

const ERR_KO = (msg) => {
  if (!msg) return '오류가 발생했습니다.';
  const s = String(msg);
  if (s.includes('OUT_OF_STOCK')) return '교환상품 재고가 부족합니다.';
  if (s.includes('CREDENTIAL_REQUIRED')) return '택배사 인증정보가 필요합니다.';
  if (s.includes('EXTERNAL_CARRIER_REQUIRED'))
    return '자동 회수 연동 전입니다. 수동으로 처리해주세요.';
  if (s.includes('403')) return '이 작업을 처리할 권한이 없습니다.';
  if (s.includes('409')) return '이미 처리된 작업입니다.';
  return s;
};

export default function OperatorDashboard() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const section = sp.get('section') || 'home';
  const [me, setMe] = useState(null);
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drawer, setDrawer] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [m, s] = await Promise.all([
          api.get('/operator/me'),
          api.get('/operator/dashboard/summary'),
        ]);
        setMe(m.data); setSummary(s.data);
      } catch (e) {
        toast.error(ERR_KO(e?.response?.data?.detail));
      }
    })();
  }, []);

  useEffect(() => {
    if (!['orders','pick','inspect','ship','pack'].includes(section)) return;
    (async () => {
      try {
        const r = await api.get('/operator/orders');
        setOrders(r.data.items || []);
      } catch (e) { toast.error(ERR_KO(e?.response?.data?.detail)); }
    })();
  }, [section]);

  const menu = useMemo(() => {
    if (!me) return [];
    const allowed = ROLE_MENU[me.role] || [];
    return allowed.map((k) => ({ key: k, label: MENU_LABELS[k] }));
  }, [me]);

  const go = (k) => { setSp({ section: k }); setDrawer(false); };

  if (!me) return (
    <div className="min-h-screen flex items-center justify-center text-sm text-neutral-500"
          data-testid="operator-dashboard-loading">
      물류직원 정보를 확인 중입니다…
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F7F8FA]" data-testid="operator-dashboard">
      <header className="bg-white border-b border-black/10 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <button className="md:hidden px-2 py-1 border rounded"
                  onClick={() => setDrawer(true)} data-testid="operator-drawer-open">☰</button>
          <div className="font-black text-[#FF3B30]">LIVEMOA 3PL</div>
          <div className="text-xs text-neutral-500 hidden sm:block" data-testid="operator-role-scope">
            {ROLE_LABEL[me.role] || me.role} · Warehouses {me.warehouse_ids.length || '전체'} · Sellers {me.seller_ids.length || '전체'}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 flex gap-4">
        {/* Sidebar */}
        <aside className="hidden md:block w-56 flex-shrink-0" data-testid="operator-sidebar">
          <div className="bg-white border border-black/10 rounded-md p-2 sticky top-16">
            {menu.map((m) => (
              <button key={m.key} onClick={() => go(m.key)}
                       data-testid={`operator-sidebar-${m.key}`}
                       className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                         section === m.key ? 'bg-[#FF3B30] text-white'
                           : 'hover:bg-black/5'}`}>{m.label}</button>
            ))}
          </div>
        </aside>

        {drawer && (
          <div className="md:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setDrawer(false)}>
            <div className="absolute right-0 top-0 h-full w-64 bg-white p-3"
                  onClick={(e) => e.stopPropagation()} data-testid="operator-drawer">
              <button onClick={() => setDrawer(false)} className="mb-2 p-1">✕</button>
              {menu.map((m) => (
                <button key={m.key} onClick={() => go(m.key)}
                         data-testid={`operator-mobile-${m.key}`}
                         className={`w-full text-left px-3 py-2 text-sm rounded-md ${
                           section === m.key ? 'bg-[#FF3B30] text-white'
                             : 'hover:bg-black/5'}`}>{m.label}</button>
              ))}
            </div>
          </div>
        )}

        {/* Main */}
        <main className="flex-1 min-w-0" data-testid="operator-main">
          {section === 'home' && <Home summary={summary} me={me} go={go} />}
          {section === 'orders' && <OrdersQueue orders={orders} filter="all" />}
          {section === 'pick' && <ScanQueue orders={orders} kind="pick" />}
          {section === 'inspect' && <ScanQueue orders={orders} kind="inspect" />}
          {section === 'ship' && <OrdersQueue orders={orders} filter="ship" testid="operator-ship-queue" />}
          {section === 'pack' && <PackQueue orders={orders} />}
          {section === 'combined-requests' && <CombinedRequestsQueue me={me} />}
          {section === 'claims' && <OperatorClaims />}
          {section === 'exchange-pick' && <OperatorClaims />}
          {section === 'carriers' && <OperatorCarrierAccounts />}
          {section === 'receiving' && <OperatorReceiving warehouseId={me.warehouse_ids?.[0]} />}
          {section === 'putaway' && <OperatorPutaway warehouseId={me.warehouse_ids?.[0]} />}
          {section === 'locations' && <OperatorLocations
                warehouseId={me.warehouse_ids?.[0]}
                canAdmin={['3PL_ADMIN','WAREHOUSE_MANAGER'].includes(me.role)} />}
          {section === 'warehouses' && <PlaceholderPanel label="창고 관리" testid="operator-warehouses"
             note="warehouse_staff.warehouse_ids 범위 내 창고만 표시됩니다." />}
          {section === 'staff' && <PlaceholderPanel label="작업자 관리" testid="operator-staff"
             note="3PL 관리자는 /admin/logistics 페이지에서 작업자를 관리합니다." link="/admin/logistics" />}
        </main>
      </div>
    </div>
  );
}

function Home({ summary, me, go }) {
  const q = summary?.queues || {};
  const cards = [
    { k: 'pick_wait',      label: '피킹 대기',     to: 'pick' },
    { k: 'inspect_wait',   label: '검수 대기',     to: 'inspect' },
    { k: 'pack_wait',      label: '포장 대기',     to: 'pack' },
    { k: 'ship_wait',      label: '출고 대기',     to: 'ship' },
    { k: 'return_receive', label: '반품 입고 대기', to: 'claims' },
    { k: 'return_inspect', label: '반품 검수 대기', to: 'claims' },
    { k: 'exchange_pick',  label: '교환 피킹',     to: 'exchange-pick' },
    { k: 'exchange_ship',  label: '교환 출고',     to: 'claims' },
  ];
  return (
    <div data-testid="operator-home">
      <h1 className="text-lg font-semibold mb-3">홈</h1>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <button key={c.k} onClick={() => go(c.to)}
                   className="bg-white border border-black/10 rounded-md p-3 text-left hover:bg-neutral-50 transition-colors"
                   data-testid={`operator-home-card-${c.k}`}>
            <div className="text-xs text-neutral-500">{c.label}</div>
            <div className="text-2xl font-black mt-1"
                  data-testid={`operator-home-count-${c.k}`}>{q[c.k] ?? 0}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function OrdersQueue({ orders, filter, testid }) {
  const filtered = orders.filter((o) => {
    // STEP 2-P7-Q · Backend `_scrub_order_for_operator` returns FLAT
    // `fulfillment_status` field (not nested `fulfillment.status`).
    // Previously read `o.fulfillment?.status` which was always undefined
    // → fallback to 'pending' → Inspect filter never matched. Also drop
    // the non-existent `picking_completed` status (canonical lifecycle:
    // pending → picking → inspected → shipped).
    const ff = o.fulfillment_status || 'pending';
    if (filter === 'pick')    return ff === 'pending';
    if (filter === 'inspect') return ff === 'picking';
    if (filter === 'ship')    return ff === 'inspected';
    return true;
  });
  return (
    <div data-testid={testid || 'operator-orders-queue'}>
      <h1 className="text-lg font-semibold mb-3">
        {filter === 'pick' ? '피킹 대기' : filter === 'inspect' ? '검수 대기'
          : filter === 'ship' ? '출고 대기' : '주문 작업'}
      </h1>
      <div className="border border-black/10 bg-white rounded-md divide-y">
        {filtered.length === 0 && (
          <div className="p-4 text-sm text-neutral-500" data-testid="operator-orders-empty">
            현재 대기 중인 작업이 없습니다.
          </div>
        )}
        {filtered.map((o) => (
          <Link key={o.seller_order_id}
                 to={`/operator/orders/${o.seller_order_id}`}
                 className="block p-3 hover:bg-neutral-50"
                 data-testid={`operator-order-row-${o.seller_order_id}`}>
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">
                  {o.order_number || o.seller_order_id}
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  seller {o.seller_id} · 상품 {o.items?.length || 0}종
                </div>
              </div>
              <span className="text-xs px-2 py-1 bg-neutral-100 rounded">
                {o.fulfillment_status || o.status}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function ScanQueue({ orders, kind }) {
  const isPick    = kind === 'pick';
  const label     = isPick ? '피킹' : '검수';
  const queueTid  = isPick ? 'operator-pick-queue' : 'operator-inspect-queue';
  const rowPrefix = isPick ? 'pick' : 'inspect';
  const targetStatus = isPick ? 'pending' : 'picking';
  // STEP 2-P7-Q · Read backend canonical flat field `fulfillment_status`
  // (see `_scrub_order_for_operator`). Prior `o.fulfillment?.status` was
  // always undefined → all rows collapsed to 'pending' fallback, so Pick
  // queue accidentally matched everything and Inspect queue matched
  // nothing.
  const rows = orders.filter((o) => (o.fulfillment_status || 'pending') === targetStatus);
  const [scanned, setScanned] = React.useState({});
  const [mode, setMode]       = React.useState({});
  // STEP 2-P7-J · Location allocation cache (per sid).
  const [alloc, setAlloc]     = React.useState({});         // sid → allocation payload
  const [locConfirmed, setLocConfirmed] = React.useState({}); // sid → {itemKey: current locId}
  const [busy, setBusy]       = React.useState({});
  React.useEffect(() => {
    if (!isPick) return;
    rows.forEach(async (o) => {
      const sid = o.seller_order_id;
      if (alloc[sid]) return;
      try {
        const r = await api.get(`/operator/orders/${sid}/pick-allocation`);
        setAlloc((p) => ({ ...p, [sid]: r.data }));
      } catch {}
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.map((r) => r.seller_order_id).join(','), isPick]);

  const totalRequired = (o) => (o.items || []).reduce((s, it) => s + (it.quantity || 0), 0);
  const totalScanned  = (sid) => Object.values(scanned[sid] || {}).reduce((s, n) => s + n, 0);

  const onScanned = (sid, pid, vk) => {
    setScanned((prev) => {
      const cur = { ...(prev[sid] || {}) };
      // Location-managed: bump per-location bucket; Legacy: bump per (pid,vk).
      const loc = (isPick && locConfirmed[sid] || {})[`${pid}::${vk}`];
      const key = loc ? `${pid}::${vk}::${loc}` : `${pid}::${vk}`;
      cur[key] = (cur[key] || 0) + 1;
      return { ...prev, [sid]: cur };
    });
    setMode((prev) => prev[sid] ? prev : { ...prev, [sid]: 'BARCODE' });
  };
  const onReset = (sid) => {
    setScanned((prev) => ({ ...prev, [sid]: {} }));
    setLocConfirmed((prev) => ({ ...prev, [sid]: {} }));
    setMode((prev) => ({ ...prev, [sid]: undefined }));
  };

  const buildLocationPicks = (sid, aData) => {
    const s = scanned[sid] || {};
    const out = [];
    for (const it of (aData?.items || [])) {
      if (!it.location_managed) continue;
      for (const a of (it.allocations || [])) {
        const key = `${it.product_id}::${it.variant_key}::${a.location_id}`;
        const q = s[key] || 0;
        if (q > 0) out.push({ location_id: a.location_id,
                              product_id: it.product_id,
                              variant_key: it.variant_key,
                              quantity: q });
      }
    }
    return out;
  };

  const complete = async (sid, aData) => {
    if (busy[sid]) return;
    setBusy((p) => ({ ...p, [sid]: true }));
    try {
      const path = isPick ? `/operator/orders/${sid}/pick`
                          : `/operator/orders/${sid}/inspect`;
      const body = { verification_mode: mode[sid] || 'MANUAL' };
      if (isPick && aData) body.location_picks = buildLocationPicks(sid, aData);
      await api.post(path, body);
      toast.success(`${label} 완료 처리했습니다.`);
      window.location.reload();
    } catch (e) {
      const st = e?.response?.status;
      const d  = e?.response?.data?.detail || '';
      if (d.startsWith('WRONG_LOCATION')) toast.error('지정된 피킹 위치가 아닙니다.');
      else if (d.startsWith('INSUFFICIENT_LOCATION_STOCK'))
        toast.error('해당 위치의 재고가 부족합니다. 새로고침 후 다시 확인해주세요.');
      else if (d.startsWith('PICK_REQUIRED_QTY_MISMATCH'))
        toast.error('피킹 수량을 다시 확인해주세요.');
      else if (d.startsWith('ALREADY_PROCESSED'))
        toast.error('이미 다른 작업자가 처리한 주문입니다.');
      else if (st === 409) toast.error('이미 처리되었거나 현재 상태에서 수행할 수 없습니다.');
      else if (st === 403) toast.error('이 작업을 처리할 권한이 없습니다.');
      else toast.error(ERR_KO(d));
    } finally {
      setBusy((p) => ({ ...p, [sid]: false }));
    }
  };

  return (
    <div data-testid={queueTid}>
      <h1 className="text-lg font-semibold mb-3">{label} 대기</h1>
      <div className="bg-white border border-black/10 rounded-md divide-y">
        {rows.length === 0 && (
          <div className="p-4 text-sm text-neutral-500"
                data-testid={`operator-${rowPrefix}-empty`}>
            현재 {label} 대기 중인 주문이 없습니다.
          </div>
        )}
        {rows.map((o) => {
          const sid = o.seller_order_id;
          const aData = isPick ? alloc[sid] : null;
          const req = totalRequired(o);
          const done = totalScanned(sid);
          const ready = done >= req && req > 0 && !busy[sid];
          return (
            <div key={sid} className="p-3"
                  data-testid={`${rowPrefix}-scan-panel-${sid}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{o.order_number || sid}</div>
                  <div className="text-xs text-neutral-500 mt-1">
                    seller {o.seller_id} · 상품 {(o.items||[]).length}종 · 확인 {done}/{req}
                  </div>
                </div>
                <span className="text-xs px-2 py-1 bg-neutral-100 rounded">
                  {o.fulfillment_status || o.status}
                </span>
              </div>
              <WarehouseScanInput
                sid={sid}
                progress={(o.items || []).map((it) => ({
                  product_id: it.product_id,
                  // STEP 2-P7-O · seller_orders.items[] has no variant_key
                  // field — derive canonical from option (see helper at top).
                  variant_key: canonicalVk(it.option),
                  product_name: it.product_name || it.title || it.name_snapshot,
                  required_qty: it.quantity || 0,
                }))}
                scanned={{
                  // Adapter for WarehouseScanInput's simpler `{pid::vk: n}` model.
                  // Aggregate across locations so the input's over-scan guard uses
                  // the total. The final backend guard is authoritative.
                  ...Object.fromEntries((o.items || []).map((it) => {
                    const s = scanned[sid] || {};
                    let sum = 0;
                    // STEP 2-P7-O · canonical from option (same as progress).
                    const prefix = `${it.product_id}::${canonicalVk(it.option)}`;
                    for (const [k, v] of Object.entries(s))
                      if (k === prefix || k.startsWith(prefix + '::')) sum += v;
                    return [prefix, sum];
                  }))
                }}
                onScanned={(pid, vk) => {
                  if (!isPick) return onScanned(sid, pid, vk);
                  const item = (aData?.items || []).find(
                    (i) => i.product_id === pid && (i.variant_key || 'default') === vk);
                  if (item?.location_managed) {
                    const conf = (locConfirmed[sid] || {})[`${pid}::${vk}`];
                    if (!conf) return toast.error('먼저 피킹 위치를 스캔해주세요.');
                    const targetKey = `${pid}::${vk}::${conf}`;
                    const cur = (scanned[sid] || {})[targetKey] || 0;
                    const targetQty = (item.allocations.find((a) => a.location_id === conf) || {}).quantity || 0;
                    if (cur >= targetQty)
                      return toast.error('해당 위치의 피킹수량을 모두 완료했습니다.');
                  }
                  onScanned(sid, pid, vk);
                }}
                onReset={() => onReset(sid)} />
              {/* Location allocations (Location-managed) */}
              {isPick && aData?.items?.filter((it) => it.location_managed).map((it) => (
                <div key={`${it.product_id}::${it.variant_key}`}
                      className="mt-2 border rounded p-2 bg-neutral-50 text-xs"
                      data-testid={`pick-location-block-${sid}-${it.product_id}`}>
                  <div className="font-medium mb-1">
                    상품 {it.product_id} · 필요수량 {it.required_qty}
                  </div>
                  {it.allocations.map((a) => {
                    const key = `${it.product_id}::${it.variant_key}::${a.location_id}`;
                    const cur = (scanned[sid] || {})[key] || 0;
                    const conf = (locConfirmed[sid] || {})[`${it.product_id}::${it.variant_key}`];
                    const active = conf === a.location_id;
                    const complete_ = cur >= a.quantity;
                    return (
                      <div key={a.location_id}
                            className={`flex items-center gap-2 px-2 py-1 rounded ${
                              active ? 'bg-emerald-50' : ''} ${complete_ ? 'text-neutral-500 line-through' : ''}`}
                            data-testid={`pick-loc-${sid}-${a.location_id}`}>
                        <button onClick={() => {
                          // Manual Location selection (fallback) — allocation-scoped only.
                          setLocConfirmed((p) => ({ ...p,
                            [sid]: { ...(p[sid] || {}),
                                     [`${it.product_id}::${it.variant_key}`]: a.location_id } }));
                          toast.success(`${a.code} 위치가 선택되었습니다.`);
                        }} data-testid={`pick-loc-select-${sid}-${a.location_id}`}
                                 className="px-2 py-0.5 border rounded">선택</button>
                        <span className="flex-1">{a.code}</span>
                        <span data-testid={`pick-loc-count-${sid}-${a.location_id}`}>
                          {cur}/{a.quantity}{complete_ ? ' ✓' : ''}
                        </span>
                        <button onClick={() => {
                          const conf2 = (locConfirmed[sid] || {})[`${it.product_id}::${it.variant_key}`];
                          if (conf2 !== a.location_id) return toast.error('먼저 위치를 선택하세요.');
                          if (cur >= a.quantity) return toast.error('해당 위치의 피킹수량을 모두 완료했습니다.');
                          setScanned((p) => {
                            const s2 = { ...(p[sid] || {}) };
                            s2[key] = Math.min(a.quantity, cur + 1);
                            return { ...p, [sid]: s2 };
                          });
                          setMode((p) => ({ ...p, [sid]: p[sid] === 'BARCODE' ? 'MIXED' : 'MANUAL' }));
                        }} data-testid={`pick-loc-plus-${sid}-${a.location_id}`}
                                 className="px-1 border rounded">+</button>
                      </div>
                    );
                  })}
                </div>
              ))}
              <div className="mt-2">
                <button onClick={() => complete(sid, aData)}
                         disabled={!ready}
                         className={`px-3 py-1 text-sm rounded ${
                           ready ? 'bg-[#FF3B30] text-white'
                             : 'bg-neutral-200 text-neutral-500'}`}
                         data-testid={`${rowPrefix}-scan-complete-${sid}`}>
                  {label} 완료 {ready ? '' : `(${done}/${req})`}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PackQueue({ orders }) {
  const [pkgCount, setPkgCount] = React.useState({});
  const [note, setNote] = React.useState({});
  // scanned = { [sid]: { `${pid}::${vk}`: count } } + verification_mode
  const [scanned, setScanned] = React.useState({});
  const [mode, setMode] = React.useState({});   // sid -> BARCODE / MANUAL / MIXED
  // STEP 2-P7-S · Read backend canonical FLAT contract
  // (`fulfillment_status`, `packing_status`). Prior nested reads on
  // `o.fulfillment.*` were always undefined because the scrub DTO
  // returns flat fields only → PackQueue was always empty.
  //
  // Include: inspected orders whose packing_status is not PACKED.
  // Legacy inspected orders (packing_status=null) are shown as
  // NOT_STARTED so operators can start packing them.
  const rows = orders.filter((o) => {
    const ff = o.fulfillment_status || 'pending';
    if (ff !== 'inspected') return false;
    const ps = o.packing_status || 'NOT_STARTED';
    if (ps === 'PACKED') return false;
    return true;
  });
  const totalRequired = (o) => (o.items || []).reduce(
    (s, it) => s + (it.quantity || 0), 0);
  const totalScanned = (sid) => Object.values(scanned[sid] || {})
    .reduce((s, n) => s + n, 0);
  const onScanned = (sid, pid, vk, matched_by) => {
    setScanned((prev) => {
      const cur = { ...(prev[sid] || {}) };
      const key = `${pid}::${vk}`;
      cur[key] = (cur[key] || 0) + 1;
      return { ...prev, [sid]: cur };
    });
    setMode((prev) => {
      const cur = prev[sid];
      if (!cur) return { ...prev, [sid]: 'BARCODE' };
      return prev;
    });
  };
  const onReset = (sid) => {
    setScanned((prev) => ({ ...prev, [sid]: {} }));
    setMode((prev) => ({ ...prev, [sid]: undefined }));
  };
  const call = async (sid, action, body) => {
    try {
      await api.post(`/operator/orders/${sid}/pack/${action}`, body || {});
      toast.success(action === 'start' ? '포장을 시작했습니다.' : '포장 완료 처리했습니다.');
      window.location.reload();
    } catch (e) {
      toast.error(ERR_KO(e?.response?.data?.detail));
    }
  };
  return (
    <div data-testid="operator-pack-queue">
      <h1 className="text-lg font-semibold mb-3">포장 대기</h1>
      <div className="bg-white border border-black/10 rounded-md divide-y">
        {rows.length === 0 && (
          <div className="p-4 text-sm text-neutral-500" data-testid="operator-pack-empty">
            현재 포장 대기 중인 주문이 없습니다.
          </div>
        )}
        {rows.map((o) => {
          const sid = o.seller_order_id;
          // STEP 2-P7-S · Read flat backend field. `o.fulfillment` is
          // not exposed by the operator DTO — use `o.packing_status`.
          const ps = o.packing_status || 'NOT_STARTED';
          const req = totalRequired(o);
          const done = totalScanned(sid);
          const readyForComplete = done >= req && req > 0;
          const progress = (o.items || []).map((it) => ({
            product_id: it.product_id,
            // STEP 2-P7-O · canonical from option (Pack queue).
            variant_key: canonicalVk(it.option),
            product_name: it.product_name || it.title || it.name_snapshot,
            required_qty: it.quantity || 0,
          }));
          return (
            <div key={sid} className="p-3" data-testid={`pack-row-${sid}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {o.order_number || sid}
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">
                    seller {o.seller_id} · 상품 {(o.items||[]).length}종 · 확인 {done}/{req}
                  </div>
                </div>
                <span className="text-xs px-2 py-1 bg-neutral-100 rounded"
                        data-testid={`pack-status-${sid}`}>{ps}</span>
              </div>
              {ps === 'NOT_STARTED' && (
                <button onClick={() => call(sid, 'start')}
                         className="px-3 py-1 text-sm bg-[#FF3B30] text-white rounded"
                         data-testid={`pack-start-${sid}`}>포장 시작</button>
              )}
              {ps === 'PACKING' && (
                <div className="space-y-2">
                  <WarehouseScanInput
                    sid={sid} progress={progress}
                    scanned={scanned[sid] || {}}
                    onScanned={(pid, vk, mb) => onScanned(sid, pid, vk, mb)}
                    onReset={() => onReset(sid)} />
                  <div className="flex flex-wrap gap-2 items-center">
                    {progress.map((p) => {
                      const key = `${p.product_id}::${p.variant_key}`;
                      const cur = (scanned[sid] || {})[key] || 0;
                      const set = (delta) => {
                        setScanned((prev) => {
                          const s2 = { ...(prev[sid] || {}) };
                          s2[key] = Math.max(0, Math.min(p.required_qty, cur + delta));
                          return { ...prev, [sid]: s2 };
                        });
                        setMode((prev) => {
                          const cur2 = prev[sid];
                          return { ...prev, [sid]: cur2 === 'BARCODE' ? 'MIXED' : 'MANUAL' };
                        });
                      };
                      return (
                        <div key={key}
                              className="border rounded px-2 py-1 text-xs"
                              data-testid={`scan-item-${p.product_id}`}>
                          <span className="mr-2">{p.product_name}</span>
                          <span data-testid={`scan-count-${p.product_id}`}>
                            {cur}/{p.required_qty}
                          </span>
                          <button onClick={() => set(-1)}
                                   className="ml-2 px-1 border rounded"
                                   data-testid={`scan-manual-minus-${p.product_id}`}>-</button>
                          <button onClick={() => set(+1)}
                                   className="ml-1 px-1 border rounded"
                                   data-testid={`scan-manual-plus-${p.product_id}`}>+</button>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 items-center">
                    <input type="number" min={1} value={pkgCount[sid] ?? 1}
                            onChange={(e) => setPkgCount((s) => ({ ...s, [sid]: Math.max(1, Number(e.target.value) || 1) }))}
                            className="w-16 border rounded px-2 py-1 text-sm"
                            data-testid={`pack-package-count-${sid}`} />
                    <input type="text" placeholder="포장 메모(선택)"
                            value={note[sid] || ''}
                            maxLength={500}
                            onChange={(e) => setNote((s) => ({ ...s, [sid]: e.target.value }))}
                            className="flex-1 border rounded px-2 py-1 text-sm"
                            data-testid={`pack-note-${sid}`} />
                    <button onClick={() => call(sid, 'complete',
                              { package_count: pkgCount[sid] ?? 1,
                                packing_note: note[sid],
                                verification_mode: mode[sid] || 'MANUAL' })}
                             disabled={!readyForComplete}
                             className={`px-3 py-1 text-sm rounded ${
                               readyForComplete ? 'bg-[#FF3B30] text-white'
                                 : 'bg-neutral-200 text-neutral-500'}`}
                             data-testid={`pack-complete-${sid}`}>
                      포장 완료 {readyForComplete ? '' : `(${done}/${req})`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// STEP 3-WMS-B PHASE 2-D · Seller Combined Shipment Request queue for Operator.
function CombinedRequestsQueue({ me }) {
  const [rows, setRows]   = React.useState([]);
  const [loading, setLd]  = React.useState(true);
  const [detailCid, setDetailCid] = React.useState(null);
  const [detail, setDetail]  = React.useState(null);
  const [busy, setBusy]      = React.useState(false);
  const [rejectOpen, setRO]  = React.useState(false);
  const [reason, setReason]  = React.useState('address_mismatch');
  const [note, setNote]      = React.useState('');

  const load = async () => {
    setLd(true);
    try {
      const r = await api.get('/operator/3pl-combined/requests');
      setRows(r.data?.items || []);
    } catch (e) { toast.error(ERR_KO(e?.response?.data?.detail)); }
    finally { setLd(false); }
  };
  React.useEffect(() => { load(); }, []);

  const openDetail = async (cid) => {
    setDetailCid(cid);
    try {
      const r = await api.get(`/operator/combined-shipments/${cid}`);
      setDetail(r.data);
    } catch (e) { toast.error(ERR_KO(e?.response?.data?.detail)); }
  };
  const doApprove = async () => {
    if (!detailCid) return;
    setBusy(true);
    try {
      await api.post(`/operator/combined-shipments/${detailCid}/approve-request`);
      toast.success('📦 합포 요청 승인 완료');
      setDetailCid(null); setDetail(null);
      await load();
    } catch (e) { toast.error(ERR_KO(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doReject = async () => {
    if (!detailCid) return;
    setBusy(true);
    try {
      await api.post(`/operator/combined-shipments/${detailCid}/reject-request`,
        { reason, note });
      toast.success('요청 거절 완료');
      setRO(false); setDetailCid(null); setDetail(null);
      await load();
    } catch (e) { toast.error(ERR_KO(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="operator-combined-requests">
      <div className="flex items-center gap-2 mb-3">
        <h1 className="text-lg font-semibold">판매자 합포 요청</h1>
        <span className="text-xs text-neutral-500" data-testid="operator-combined-requests-count">
          대기 {rows.length}건
        </span>
      </div>
      <div className="border border-black/10 bg-white rounded-md divide-y">
        {loading && <div className="p-4 text-sm text-neutral-500">불러오는 중…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-4 text-sm text-neutral-500" data-testid="operator-combined-requests-empty">
            현재 대기 중인 판매자 합포 요청이 없습니다.
          </div>
        )}
        {rows.map((c) => (
          <button key={c.combined_shipment_id} onClick={() => openDetail(c.combined_shipment_id)}
                   className="block w-full text-left p-3 hover:bg-neutral-50"
                   data-testid={`operator-request-row-${c.combined_shipment_id}`}>
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">
                  📦 {c.recipient_snapshot?.recipient_name} · {(c.members || []).length}건
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  seller {c.seller_id} · warehouse {c.warehouse_id}
                </div>
              </div>
              <span className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded font-bold">
                {c.status}
              </span>
            </div>
          </button>
        ))}
      </div>

      {detailCid && detail && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4"
             onClick={() => !busy && (setDetailCid(null), setDetail(null))}>
          <div className="bg-white rounded-lg max-w-lg w-full p-4 space-y-3 max-h-[90vh] overflow-auto"
               onClick={(e) => e.stopPropagation()} data-testid="operator-request-detail">
            <div className="flex items-center justify-between">
              <h3 className="font-bold">📦 합포 요청 상세</h3>
              <button onClick={() => { setDetailCid(null); setDetail(null); }} disabled={busy}>✕</button>
            </div>
            <div className="text-xs bg-neutral-50 p-2 rounded">
              <p><b>수령인:</b> {detail.recipient_snapshot?.recipient_name}</p>
              <p><b>전화:</b> {detail.recipient_snapshot?.phone}</p>
              <p><b>주소:</b> [{detail.recipient_snapshot?.postal_code}] {detail.recipient_snapshot?.address1} {detail.recipient_snapshot?.address2}</p>
              <p><b>창고:</b> <span className="font-mono">{detail.warehouse_id}</span></p>
              <p><b>요청일시:</b> {detail.requested_at}</p>
            </div>
            <div className="text-xs">
              <p className="font-bold mb-1">포함 주문 {(detail.members || []).length}건</p>
              <ul className="space-y-1 font-mono">
                {(detail.members || []).map((m) => (
                  <li key={m.seller_order_id} className="border rounded px-2 py-1">
                    {m.seller_order_id}
                  </li>
                ))}
              </ul>
            </div>
            {!rejectOpen ? (
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setRO(true)} disabled={busy}
                        className="px-3 py-1.5 rounded border text-sm text-red-700 border-red-300"
                        data-testid="operator-request-reject-open">합포 요청 거절</button>
                <button onClick={doApprove} disabled={busy}
                        className="px-3 py-1.5 rounded bg-[#FF3B30] text-white text-sm font-bold disabled:opacity-50"
                        data-testid="operator-request-approve-btn">
                  📦 합포 승인
                </button>
              </div>
            ) : (
              <div className="space-y-2 border-t pt-2">
                <p className="text-xs font-bold">거절 사유</p>
                <select value={reason} onChange={(e) => setReason(e.target.value)}
                        className="w-full border rounded px-2 py-1 text-xs"
                        data-testid="operator-request-reject-reason">
                  <option value="address_mismatch">배송지 불일치</option>
                  <option value="already_packed">이미 포장/출고 진행</option>
                  <option value="different_warehouse">창고 상이</option>
                  <option value="order_status_mismatch">주문 상태 불일치</option>
                  <option value="other">기타</option>
                </select>
                <textarea value={note} onChange={(e) => setNote(e.target.value)}
                          placeholder="상세 사유 (선택)"
                          className="w-full border rounded px-2 py-1 text-xs min-h-[60px]"
                          data-testid="operator-request-reject-note"/>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setRO(false)} disabled={busy} className="px-3 py-1.5 rounded border text-sm">취소</button>
                  <button onClick={doReject} disabled={busy}
                          className="px-3 py-1.5 rounded bg-red-600 text-white text-sm disabled:opacity-50"
                          data-testid="operator-request-reject-btn">거절 확정</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function PlaceholderPanel({ label, note, link, testid }) {
  return (
    <div data-testid={testid}>
      <h1 className="text-lg font-semibold mb-3">{label}</h1>
      <div className="bg-white border border-black/10 rounded-md p-4 text-sm text-neutral-600">
        {note}
        {link && (
          <div className="mt-3">
            <Link to={link} className="text-[#FF3B30] underline"
                   data-testid={`${testid}-link`}>{link}</Link>
          </div>
        )}
      </div>
    </div>
  );
}
